import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { checkUsageLimit, UsageLimitExceededError, incrementUsage } from "@/hooks/useUsageLimits";
import { trackEvent } from "@/lib/analytics";
import { getContentSourceText } from "@/lib/content/getContentSourceText";
import type { ContentType } from "@/types/content";
import { resolveModel } from "@/lib/ai/models";

const BASE_SYSTEM_PROMPT = `You are Shattara AI, a friendly, focused study tutor for high school and university students in Saudi Arabia and the MENA region.

Core behavior:
- Explain concepts clearly, step-by-step, using simple language first, then add deeper detail if needed.
- Encourage active learning: ask short follow-up questions, propose practice questions, and suggest ways to check understanding.
- Adapt depth and tone to the student's level based on how they ask the question.
- When a question is ambiguous, briefly ask a clarifying question instead of guessing.
- When the student is stuck, break the problem into small steps and guide them through.
- Prioritize correctness over creativity. If you are unsure, say so and suggest how the student can verify.

Content handling:
- Use the provided content as your PRIMARY source when answering questions.
- Reference specific parts of the content when relevant.
- If the student asks about something not covered in the content, say so clearly.
- Quote or paraphrase from the content to support your explanations.
- If there is a conflict between your general knowledge and the provided material, explain both and say which is which.
- For math or science derivations, always show key steps, not just the final answer.

Safety and integrity:
- Do not write full graded assignments or essays for the student. Instead, help them outline, brainstorm, and improve their own work.
- Do not invent citations, references, or fake sources.
- Avoid harmful, hateful, or adult content and redirect to safe, educational topics.

Formatting:
- Use short paragraphs and bullets when it helps readability.
- Use inline LaTeX-style math (like x^2 + y^2) only when it's helpful, not everywhere.
- For definitions or procedures, you can use numbered steps (1., 2., 3., …).

Always keep your goal in mind:
Help the student understand, not just memorize; make them feel more confident about studying.

Study tools:
When the user mentions @Mind Map, respond with a mindmap code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the mind map.
IMPORTANT constraints:
- Maximum 4-5 top-level nodes (branches from root). Never exceed 5.
- Maximum 2-3 children per node. Prioritize the most important points.
- Keep labels short (1-3 words).

\`\`\`mindmap
{
  "title": "Topic Title",
  "root": "Central Concept",
  "nodes": [
    { "id": "1", "label": "Branch 1", "children": [
      { "id": "1a", "label": "Sub-point A" },
      { "id": "1b", "label": "Sub-point B" }
    ]},
    { "id": "2", "label": "Branch 2" }
  ],
  "description": "Brief summary of the mind map."
}
\`\`\`

When the user mentions @Timeline, respond with a timeline code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the timeline.
IMPORTANT constraints:
- Maximum 8-10 items. Prioritize the most significant events.
- Keep titles short (2-5 words).
- Dates should be concise (e.g. "1889", "1914-1918", "June 1944").

\`\`\`timeline
{
  "title": "Timeline Title",
  "items": [
    { "id": "1", "date": "1900", "title": "Event Title", "description": "Brief description" },
    { "id": "2", "date": "1950", "title": "Another Event", "description": "What happened" }
  ]
}
\`\`\`

When the user mentions @Flowchart, respond with a flowchart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the flowchart.
IMPORTANT constraints:
- Maximum 8-12 nodes. Prioritize key steps and decision points.
- Use "rect" shape for events/actions, "diamond" for yes/no decisions.
- Keep labels short (1-3 words per line, max 3 lines).
- Use colors meaningfully: blue for start, green for positive, purple for actions, orange for transitions, yellow for outcomes.
- Edges connect nodes by id. Add "label" only for decision branches (e.g. "Yes", "No").

\`\`\`flowchart
{
  "title": "Process Title",
  "nodes": [
    { "id": "A", "label": ["Step One", "Detail"], "shape": "rect", "color": "blue" },
    { "id": "B", "label": ["Decision?"], "shape": "diamond", "color": "green" },
    { "id": "C", "label": ["Result"], "shape": "rect", "color": "purple" }
  ],
  "edges": [
    { "from": "A", "to": "B" },
    { "from": "B", "to": "C", "label": "Yes" }
  ]
}
\`\`\`

When the user mentions @Bar Chart, respond with a barchart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the chart.
IMPORTANT constraints:
- Maximum 10 data points (labels). Prioritize the most significant values.
- Keep labels short (1-4 words).
- Keep axis labels concise (1-3 words).
- Use descriptive dataset labels.
- Default color is "#f59e0b" (amber). Use different colors for multiple datasets.

\`\`\`barchart
{
  "title": "Chart Title",
  "xLabel": "Category",
  "yLabel": "Value",
  "labels": ["Item A", "Item B", "Item C"],
  "datasets": [
    { "label": "Count", "data": [10, 25, 15], "color": "#f59e0b" }
  ]
}
\`\`\`

When the user mentions @Line Chart, respond with a linechart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the chart.
IMPORTANT constraints:
- Maximum 10 data points (labels). Prioritize the most significant values.
- Keep labels short (1-4 words).
- Keep axis labels concise (1-3 words).
- Use descriptive dataset labels.
- Default color is "#f59e0b" (amber). Use different colors for multiple datasets.
- Supports multiple lines (datasets) for comparison.

\`\`\`linechart
{
  "title": "Chart Title",
  "xLabel": "Time",
  "yLabel": "Value",
  "labels": ["Jan", "Feb", "Mar"],
  "datasets": [
    { "label": "Series A", "data": [10, 25, 15], "color": "#f59e0b" }
  ]
}
\`\`\`

When the user mentions @Pie Chart, respond with a piechart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the chart.
IMPORTANT constraints:
- Maximum 8 slices. Combine smaller items into "Other" if needed.
- Keep labels short (1-4 words).
- Data values can be percentages or absolute numbers.
- Default colors: "#f59e0b", "#ef4444", "#3b82f6", "#10b981", "#f97316".

\`\`\`piechart
{
  "title": "Chart Title",
  "labels": ["Category A", "Category B", "Category C"],
  "data": [50, 30, 20]
}
\`\`\`

When the user mentions @Venn Diagram, respond with a venndiagram code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the diagram.
IMPORTANT constraints:
- Maximum 2-3 circles (sets). Never exceed 3.
- Maximum 8 intersection items total.
- Keep labels short (1-5 words).
- Always provide distinct colors for each circle.
- Each intersection item references the set(s) it belongs to.

\`\`\`venndiagram
{
  "title": "Diagram Title",
  "circles": [
    { "id": "A", "label": "Set A", "color": "#f59e0b" },
    { "id": "B", "label": "Set B", "color": "#8b5cf6" }
  ],
  "intersections": [
    { "sets": ["A"], "label": "Only in A" },
    { "sets": ["A", "B"], "label": "Shared item" },
    { "sets": ["B"], "label": "Only in B" }
  ]
}
\`\`\``;

const MAX_CONTENT_LENGTH = 120000;

function buildContentSystemPrompt(contentText: string): string {
  const truncated = contentText.length > MAX_CONTENT_LENGTH
    ? contentText.slice(0, MAX_CONTENT_LENGTH) + "\n\n[Content truncated for length]"
    : contentText;

  return `${BASE_SYSTEM_PROMPT}

## Study Material Context
You have been given the following content that the student is studying. Use this as your PRIMARY source when answering questions. Help the student understand and remember key concepts from this specific material.

<content>
${truncated}
</content>`;
}

interface ContentChatSessionOptions {
  chatId: string | undefined;
  contentId: string | undefined;
  contentType: ContentType | undefined;
}

export function useContentChatSession({ chatId, contentId, contentType }: ContentChatSessionOptions) {
  const [messages, setMessages] = useState<any[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasTriggeredAI, setHasTriggeredAI] = useState(false);
  const [contentText, setContentText] = useState<string | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Cache content text to avoid refetching
  const contentTextRef = useRef<string | null>(null);

  // Load content source text
  useEffect(() => {
    const loadContentText = async () => {
      if (!contentId || !contentType) {
        setIsLoadingContent(false);
        return;
      }

      // Skip if already cached
      if (contentTextRef.current !== null) {
        setContentText(contentTextRef.current);
        setIsLoadingContent(false);
        return;
      }

      setIsLoadingContent(true);
      try {
        const text = await getContentSourceText(contentId, contentType);
        contentTextRef.current = text;
        setContentText(text);
      } catch (error) {
        console.error("Error loading content text:", error);
      } finally {
        setIsLoadingContent(false);
      }
    };

    loadContentText();
  }, [contentId, contentType]);

  // Load latest conversation for this content
  const loadHistory = useCallback(async () => {
    if (!chatId) return;

    // Get the most recent conversation_id for this content
    const { data: latestMsg, error: latestError } = await supabase
      .from("chat_messages")
      .select("conversation_id")
      .eq("content_id", chatId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latestError) {
      console.error("Error loading latest conversation:", latestError);
      return;
    }

    if (!latestMsg) {
      setMessages([]);
      setConversationId(null);
      return;
    }

    const convId = latestMsg.conversation_id;
    setConversationId(convId);

    const { data, error } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("content_id", chatId)
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error loading chat history:", error);
      return;
    }

    if (data) {
      setMessages(data);
    }
  }, [chatId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Auto-trigger AI if last message is from user
  useEffect(() => {
    if (!chatId || !messages.length || hasTriggeredAI || isStreaming || isLoadingContent) return;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role === 'user') {
      setHasTriggeredAI(true);
      triggerAIResponse();
    }
  }, [messages, chatId, hasTriggeredAI, isStreaming, isLoadingContent]);

  const getSystemPrompt = useCallback(() => {
    if (contentText) {
      return buildContentSystemPrompt(contentText);
    }
    return BASE_SYSTEM_PROMPT;
  }, [contentText]);

  const triggerAIResponse = useCallback(async () => {
    if (!chatId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setIsStreaming(true);

    try {
      // Build full history for LLM with content-aware system prompt
      const history = [
        { role: "system", content: getSystemPrompt() },
        ...messages.map((m) => ({
          role: m.role,
          content: m.message,
        })),
      ];

      // Prepare assistant message placeholder
      let assistantText = "";
      const collectedAnnotations: any[] = [];
      const streamingId = "streaming-" + Date.now();
      
      setMessages((prev) => [
        ...prev,
        { id: streamingId, role: "assistant", message: "", created_at: new Date().toISOString() },
      ]);

      // Stream AI tokens
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No active session");

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/chat-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ messages: history, searchEnabled: false }),
      });

      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }

      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });
        
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantText += content;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamingId ? { ...m, message: assistantText } : m
                )
              );
            }
            const annotations = parsed.choices?.[0]?.delta?.annotations;
            if (annotations && Array.isArray(annotations)) {
              collectedAnnotations.push(...annotations);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      const { data: aiMessage, error: aiError } = await supabase
        .from("chat_messages")
        .insert({
          content_id: chatId,
          user_id: user.id,
          role: "assistant",
          message: assistantText,
          metadata: collectedAnnotations.length > 0 ? { annotations: collectedAnnotations } : null,
          conversation_id: conversationId!,
        })
        .select()
        .single();

      if (aiError) throw aiError;

      setMessages((prev) =>
        prev.map((m) => (m.id === streamingId ? aiMessage : m))
      );
    } catch (error) {
      console.error("Error getting AI response:", error);
      toast({
        title: "Error",
        description: "Failed to get AI response. Please try again.",
        variant: "destructive",
      });
      setMessages((prev) => prev.filter((m) => !m.id?.toString().startsWith("streaming-")));
    } finally {
      setIsStreaming(false);
    }
  }, [chatId, messages, toast, getSystemPrompt, conversationId]);

  // Send new message (user → AI)
  const sendMessage = useCallback(
    async (userText: string, options?: { searchEnabled?: boolean; model?: string }) => {
      if (!chatId) {
        toast({
          title: "Error",
          description: "Chat ID is missing",
          variant: "destructive",
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to send messages",
          variant: "destructive",
        });
        return;
      }

      // Check usage limit before sending
      const limitCheck = await checkUsageLimit("chat_messages");
      if (!limitCheck.canProceed) {
        throw new UsageLimitExceededError("chat_messages", limitCheck.used, limitCheck.limit);
      }

      setIsStreaming(true);

      try {
        // Increment usage for chat message
        await incrementUsage("chat_messages");
        trackEvent("chat_started", { content_id: contentId, content_type: contentType });

        // Create new conversation if needed
        const activeConversationId = conversationId || crypto.randomUUID();
        if (!conversationId) {
          setConversationId(activeConversationId);
        }

        // Insert user message
        const { data: userMessage, error: userError } = await supabase
          .from("chat_messages")
          .insert({
            content_id: chatId,
            user_id: user.id,
            role: "user",
            message: userText,
            conversation_id: activeConversationId,
          })
          .select()
          .single();

        if (userError) throw userError;

        setMessages((prev) => [...prev, userMessage]);

        // Build full history for LLM with content-aware system prompt
        const history = [
          { role: "system", content: getSystemPrompt() },
          ...messages.map((m) => ({
            role: m.role,
            content: m.message,
          })),
          { role: "user", content: userText },
        ];

        // Prepare assistant message placeholder
        let assistantText = "";
        const collectedAnnotations: any[] = [];
        const streamingId = "streaming-" + Date.now();
        
        setMessages((prev) => [
          ...prev,
          { id: streamingId, role: "assistant", message: "", created_at: new Date().toISOString() },
        ]);

        // Stream AI tokens
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("No active session");

        const resp = await fetch(`${SUPABASE_URL}/functions/v1/chat-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: history,
            searchEnabled: options?.searchEnabled || false,
            model: resolveModel(options?.model || "auto"),
          }),
        });

        if (!resp.ok) {
          throw new Error(`HTTP error! status: ${resp.status}`);
        }

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let textBuffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          textBuffer += decoder.decode(value, { stream: true });
          
          // Process line-by-line as data arrives
          let newlineIndex: number;
          while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
            let line = textBuffer.slice(0, newlineIndex);
            textBuffer = textBuffer.slice(newlineIndex + 1);

            if (line.endsWith("\r")) line = line.slice(0, -1);
            if (line.startsWith(":") || line.trim() === "") continue;
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.slice(6).trim();
            if (jsonStr === "[DONE]") continue;

            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (content) {
                assistantText += content;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === streamingId
                      ? { ...m, message: assistantText }
                      : m
                  )
                );
              }
              const annotations = parsed.choices?.[0]?.delta?.annotations;
              if (annotations && Array.isArray(annotations)) {
                collectedAnnotations.push(...annotations);
              }
            } catch {
              // Incomplete JSON split across chunks: put it back and wait for more data
              textBuffer = line + "\n" + textBuffer;
              break;
            }
          }
        }

        // Replace streaming placeholder with real DB row
        const { data: aiMessage, error: aiError } = await supabase
          .from("chat_messages")
          .insert({
            content_id: chatId,
            user_id: user.id,
            role: "assistant",
            message: assistantText,
            metadata: collectedAnnotations.length > 0 ? { annotations: collectedAnnotations } : null,
            conversation_id: activeConversationId,
          })
          .select()
          .single();

        if (aiError) throw aiError;

        setMessages((prev) =>
          prev.map((m) => (m.id === streamingId ? aiMessage : m))
        );
      } catch (error) {
        console.error("Error sending message:", error);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
        
        // Remove streaming placeholder on error
        setMessages((prev) => prev.filter((m) => !m.id?.toString().startsWith("streaming-")));
      } finally {
        setIsStreaming(false);
      }
    },
    [chatId, messages, toast, getSystemPrompt, conversationId]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null); // Next sendMessage will create a new conversation
  }, []);

  const loadConversation = useCallback(async (convId: string) => {
    if (!chatId) return;
    setConversationId(convId);
    const { data, error } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("content_id", chatId)
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true });
    if (error) {
      console.error("Error loading conversation:", error);
      return;
    }
    if (data) setMessages(data);
  }, [chatId]);

  return {
    messages,
    isStreaming,
    sendMessage,
    reload: loadHistory,
    clearMessages,
    loadConversation,
    isLoadingContent,
    hasContentContext: !!contentText,
  };
}
